// Color engine (PLAN §12.2): the "Color by" modes and the admin's ordered color rules.
// One evaluator serves the server (the cached `entities.computed_style`), the rule editor's live preview and
// every view (lists, pickers, profiles, graphs), so an entity has the same color and the same "why this color?"
// everywhere. Colors are entity palette slots (tokens.ts), never color values.

import { REVIEW_STATUSES, type ReviewStatus } from './claims';
import { type ClusterRef, clusterWhy } from './clusters';
import { ENTITY_SLOTS } from './design/tokens';
import type { FuzzyDate } from './fuzzy-date';
import { effectiveColorSlot, typeChain } from './schema/resolve';
import { ENTITY_KIND_LABELS, ENTITY_KINDS, type EntityKind, type SchemaSnapshot } from './schema/types';
import type { MoneyRange, RatingValue, RefValue } from './schema/values';
import { SENSITIVITIES, type Sensitivity } from './uploads';

/** "Color by" modes (PLAN §12.2): type (default), cluster (the cluster job, task 34) and rules. */
export const COLOR_MODES = ['type', 'cluster', 'rules'] as const;
export type ColorMode = (typeof COLOR_MODES)[number];
export const isColorMode = (v: unknown): v is ColorMode => COLOR_MODES.includes(v as ColorMode);
export const COLOR_MODE_LABELS: Record<ColorMode, string> = { type: 'Type', cluster: 'Cluster', rules: 'Rules' };

/** What a matching rule does: set the fill color, fade the entity, or add a highlight ring. */
export const COLOR_ACTIONS = ['color', 'fade', 'ring'] as const;
export type ColorAction = (typeof COLOR_ACTIONS)[number];
export const COLOR_ACTION_LABELS: Record<ColorAction, string> = { color: 'Set color', fade: 'Fade', ring: 'Highlight ring' };

export const FIELD_OPS = ['is', 'exists', 'contains', 'gt', 'lt', 'after', 'before'] as const;
export type FieldOp = (typeof FIELD_OPS)[number];
export const FIELD_OP_LABELS: Record<FieldOp, string> = {
  is: 'is',
  exists: 'has a value',
  contains: 'contains',
  gt: 'is more than',
  lt: 'is less than',
  after: 'is after',
  before: 'is before',
};

/**
 * One condition of a rule. `not` inverts it. `label` is a display copy of what a key or id points at (tag label,
 * entity name) so the "why" text reads well without another lookup.
 */
export type RuleClause = { not?: boolean; label?: string | null } & (
  | { on: 'type'; value: string }
  | { on: 'kind'; value: EntityKind }
  | { on: 'field'; field: string; op: FieldOp; value?: string | number | null }
  | { on: 'tag'; value: string }
  | {
      on: 'linked';
      /** Relation type key (or table field key); any when empty. */
      relation?: string | null;
      /** The entity on the other end ("linked to Actor X"); any when empty. */
      entityId?: string | null;
      direction?: 'out' | 'in' | 'any';
      /** "donated > 10k": the link's amount must be more than this. */
      minAmount?: number | null;
    }
  | { on: 'status'; value: ReviewStatus }
  | { on: 'sensitivity'; value: Sensitivity }
  | { on: 'confidence'; op: 'lt' | 'gt'; value: number }
);
export type ClauseKind = RuleClause['on'];
export const CLAUSE_KINDS: ClauseKind[] = ['type', 'kind', 'field', 'tag', 'linked', 'status', 'sensitivity', 'confidence'];
export const CLAUSE_KIND_LABELS: Record<ClauseKind, string> = {
  type: 'Type',
  kind: 'Kind',
  field: 'Field value',
  tag: 'Tag',
  linked: 'Linked to',
  status: 'Review status',
  sensitivity: 'Sensitivity',
  confidence: 'AI confidence',
};

export interface RuleCondition {
  match: 'all' | 'any';
  clauses: RuleClause[];
}

export interface ColorRule {
  id: string;
  /** Position in the list (first match wins); rules are numbered #1, #2… in this order. */
  order: number;
  name: string | null;
  condition: RuleCondition;
  action: ColorAction;
  /** Palette slot for `color` (and the ring's color); null = the list value's color when `colorFromField` is set. */
  colorSlot: number | null;
  /** "Color by Camp": take the color of this list field's value (colors edited with the list). */
  colorFromField: string | null;
  enabled: boolean;
  updatedAt: number;
}

export type ColorRuleInput = Pick<ColorRule, 'name' | 'condition' | 'action' | 'colorSlot' | 'colorFromField' | 'enabled'>;

/** Everything a rule can look at, taken from the entity's current, non-rejected claims. */
export interface StyleFacts {
  id: string;
  kind: EntityKind;
  typeId: string;
  reviewStatus: ReviewStatus;
  sensitivity: Sensitivity;
  /** Field key → current values (list keys, text, numbers, dates, amounts). */
  fields: Record<string, unknown[]>;
  /** Tag keys. */
  tags: string[];
  links: { predicate: string; otherId: string; direction: 'out' | 'in'; amount: number | null }[];
  /** Average AI confidence of claims that carry one; null when none does. */
  confidence: number | null;
}

/** An entity's computed look in one mode: fill slot, fade, ring, and why. */
export interface EntityStyle {
  colorSlot: number | null;
  /** "Rule #3: Tag = Donor", "Type: Media". */
  why: string;
  fade: boolean;
  fadeWhy: string | null;
  ring: boolean;
  ringSlot: number | null;
  ringWhy: string | null;
}

export const NO_RULE_MATCHED = 'Color by rules: no rule matched';

// ─── Validation ──────────────────────────────────────────────────────────────

const isSlot = (v: unknown): v is number => Number.isInteger(v) && (v as number) >= 1 && (v as number) <= ENTITY_SLOTS;
const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const num = (v: unknown) => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v.replace(/[\s,]/g, '')) : Number.NaN;
  return Number.isFinite(n) ? n : null;
};
const DATE_PREFIX = /^\d{4}(-\d{2}(-\d{2})?)?$/;

export const MAX_RULES = 50;
export const MAX_CLAUSES = 10;

/** Checks and normalizes a rule against the schema; returns plain-language problems. */
export function checkColorRule(
  input: unknown,
  schema: Pick<SchemaSnapshot, 'types' | 'fields' | 'lists' | 'relationTypes'>,
): { ok: true; rule: ColorRuleInput } | { ok: false; errors: string[] } {
  const errors: string[] = [];
  const b = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const action = b.action ?? 'color';
  if (!COLOR_ACTIONS.includes(action as ColorAction)) errors.push('Pick what the rule does: set color, fade or highlight ring.');
  const colorSlot = b.colorSlot === undefined || b.colorSlot === null ? null : b.colorSlot;
  if (colorSlot !== null && !isSlot(colorSlot)) errors.push(`Pick a color from the palette (1–${ENTITY_SLOTS}).`);
  const colorFromField = str(b.colorFromField);
  if (colorFromField) {
    const f = schema.fields.find(x => x.key === colorFromField);
    if (!f || (f.dataType !== 'enum' && f.dataType !== 'multi_enum')) errors.push('"Color from the list" needs a list field (e.g. Camp).');
  }
  if (action === 'color' && colorSlot === null && !colorFromField) errors.push('Pick a color, or take it from a list field.');
  const cond = (b.condition && typeof b.condition === 'object' ? b.condition : {}) as Record<string, unknown>;
  const match = cond.match === 'any' ? 'any' : 'all';
  const raw = Array.isArray(cond.clauses) ? cond.clauses : [];
  if (raw.length > MAX_CLAUSES) errors.push(`A rule can have at most ${MAX_CLAUSES} conditions.`);
  const clauses: RuleClause[] = [];
  for (const [i, r] of raw.slice(0, MAX_CLAUSES).entries()) {
    const c = checkClause(r, schema);
    if (typeof c === 'string') errors.push(`Condition ${i + 1}: ${c}`);
    else clauses.push(c);
  }
  if (!raw.length && !colorFromField) errors.push('Add at least one condition.');
  if (errors.length) return { ok: false, errors };
  const name = str(b.name);
  return {
    ok: true,
    rule: {
      name: name ? name.slice(0, 80) : null,
      condition: { match, clauses },
      action: action as ColorAction,
      colorSlot: colorSlot as number | null,
      colorFromField: action === 'color' ? colorFromField : null,
      enabled: b.enabled !== false,
    },
  };
}

function checkClause(r: unknown, schema: Pick<SchemaSnapshot, 'types' | 'fields' | 'lists' | 'relationTypes'>): RuleClause | string {
  if (!r || typeof r !== 'object') return 'not a condition.';
  const c = r as Record<string, unknown>;
  const base = { ...(c.not === true ? { not: true } : {}), ...(str(c.label) ? { label: (str(c.label) as string).slice(0, 120) } : {}) };
  switch (c.on) {
    case 'type':
      return schema.types.some(t => t.key === c.value) ? { ...base, on: 'type', value: c.value as string } : 'pick a type.';
    case 'kind':
      return ENTITY_KINDS.includes(c.value as EntityKind) ? { ...base, on: 'kind', value: c.value as EntityKind } : 'pick a kind.';
    case 'field': {
      const f = schema.fields.find(x => x.key === c.field);
      if (!f) return 'pick a field.';
      const op = (c.op ?? 'is') as FieldOp;
      if (!FIELD_OPS.includes(op)) return 'pick how to compare.';
      if (op === 'exists') return { ...base, on: 'field', field: f.key, op };
      if (op === 'gt' || op === 'lt') {
        const n = num(c.value);
        return n === null ? 'enter a number.' : { ...base, on: 'field', field: f.key, op, value: n };
      }
      if (op === 'after' || op === 'before') {
        const d = str(c.value);
        return d && DATE_PREFIX.test(d)
          ? { ...base, on: 'field', field: f.key, op, value: d }
          : 'enter a date as 2021, 2021-10 or 2021-10-06.';
      }
      const v = str(c.value);
      if (!v) return 'enter a value.';
      if (op === 'is' && (f.dataType === 'enum' || f.dataType === 'multi_enum')) {
        const list = schema.lists.find(l => l.key === f.enumList);
        if (list && !list.values.some(x => x.key === v)) return `"${v}" is not a value of ${list.label}.`;
      }
      return { ...base, on: 'field', field: f.key, op, value: v.slice(0, 200) };
    }
    case 'tag': {
      const v = str(c.value);
      return v ? { ...base, on: 'tag', value: v.toLowerCase().slice(0, 64) } : 'pick a tag.';
    }
    case 'linked': {
      const relation = str(c.relation);
      if (relation && !schema.relationTypes.some(x => x.key === relation) && !schema.fields.some(x => x.key === relation))
        return 'pick a relation type.';
      const entityId = str(c.entityId);
      const minAmount = c.minAmount === undefined || c.minAmount === null || c.minAmount === '' ? null : num(c.minAmount);
      if (c.minAmount !== undefined && c.minAmount !== null && c.minAmount !== '' && minAmount === null)
        return 'the amount must be a number.';
      const direction = c.direction === 'out' || c.direction === 'in' ? c.direction : 'any';
      if (!relation && !entityId && minAmount === null) return 'pick a relation type, an entity or an amount.';
      return { ...base, on: 'linked', relation, entityId, direction, minAmount };
    }
    case 'status':
      return REVIEW_STATUSES.includes(c.value as ReviewStatus)
        ? { ...base, on: 'status', value: c.value as ReviewStatus }
        : 'pick a review status.';
    case 'sensitivity':
      return SENSITIVITIES.includes(c.value as Sensitivity)
        ? { ...base, on: 'sensitivity', value: c.value as Sensitivity }
        : 'pick a sensitivity.';
    case 'confidence': {
      const n = num(c.value);
      if (n === null || n < 0 || n > 1) return 'confidence is between 0 and 1 (e.g. 0.5).';
      return { ...base, on: 'confidence', op: c.op === 'gt' ? 'gt' : 'lt', value: n };
    }
    default:
      return 'pick what to look at.';
  }
}

// ─── Descriptions ("why this color?") ────────────────────────────────────────

type Snap = Pick<SchemaSnapshot, 'types' | 'fields' | 'lists' | 'relationTypes'>;

const listValueLabel = (schema: Snap, fieldKey: string, key: unknown) => {
  const f = schema.fields.find(x => x.key === fieldKey);
  const list = f?.enumList ? schema.lists.find(l => l.key === f.enumList) : undefined;
  return list?.values.find(v => v.key === key)?.label ?? String(key);
};

/** "Tag = Donor", "Camp = Adverse", "linked to Actor X by donated to (> 10,000)". */
export function describeClause(c: RuleClause, schema: Snap): string {
  const not = c.not ? 'not ' : '';
  switch (c.on) {
    case 'type':
      return `Type ${c.not ? '≠' : '='} ${schema.types.find(t => t.key === c.value)?.label ?? c.value}`;
    case 'kind':
      return `Kind ${c.not ? '≠' : '='} ${ENTITY_KIND_LABELS[c.value] ?? c.value}`;
    case 'field': {
      const label = schema.fields.find(f => f.key === c.field)?.label ?? c.field;
      if (c.op === 'exists') return `${label} ${c.not ? 'is empty' : 'has a value'}`;
      if (c.op === 'is') return `${label} ${c.not ? '≠' : '='} ${listValueLabel(schema, c.field, c.value)}`;
      const sym = { gt: '>', lt: '<', after: 'after', before: 'before', contains: 'contains' }[c.op];
      const v = typeof c.value === 'number' ? c.value.toLocaleString('en') : c.value;
      return `${label} ${not}${sym} ${v}`;
    }
    case 'tag':
      return `Tag ${c.not ? '≠' : '='} ${c.label ?? c.value}`;
    case 'linked': {
      const rel = c.relation
        ? (schema.relationTypes.find(r => r.key === c.relation)?.label ??
          schema.fields.find(f => f.key === c.relation)?.label ??
          c.relation)
        : null;
      const parts = [c.entityId ? `linked to ${c.label ?? 'a chosen entity'}` : 'linked', rel ? `by “${rel}”` : null];
      if (c.minAmount !== null && c.minAmount !== undefined) parts.push(`> ${c.minAmount.toLocaleString('en')}`);
      return `${c.not ? 'not ' : ''}${parts.filter(Boolean).join(' ')}`;
    }
    case 'status':
      return `Review status ${c.not ? '≠' : '='} ${c.value}`;
    case 'sensitivity':
      return `Sensitivity ${c.not ? '≠' : '='} ${c.value}`;
    case 'confidence':
      return `${not}confidence ${c.op === 'lt' ? '<' : '>'} ${c.value}`;
  }
}

export function describeCondition(cond: RuleCondition, schema: Snap): string {
  if (!cond.clauses.length) return 'every entity';
  return cond.clauses.map(c => describeClause(c, schema)).join(cond.match === 'any' ? ' or ' : ' and ');
}

/** "Rule #3: Tag = Donor" (or the rule's name). */
export const ruleLabel = (rule: Pick<ColorRule, 'name' | 'condition'>, n: number, schema: Snap) =>
  `Rule #${n}: ${rule.name ?? describeCondition(rule.condition, schema)}`;

// ─── Evaluation ──────────────────────────────────────────────────────────────

const lower = (v: unknown) => (typeof v === 'string' ? v.toLowerCase() : v);

/** A comparable number for amounts and numbers (a range counts by its lower end). */
function numberOf(v: unknown): number | null {
  if (typeof v === 'number') return v;
  if (v && typeof v === 'object' && 'qualifier' in v) {
    const m = v as MoneyRange;
    return m.min ?? m.max ?? null;
  }
  if (v && typeof v === 'object' && 'justification' in v) return num((v as RatingValue).value);
  return typeof v === 'string' ? num(v) : null;
}

/** ISO prefix of a date value (the start of a fuzzy date). */
function dateOf(v: unknown): string | null {
  if (typeof v === 'string' && DATE_PREFIX.test(v)) return v;
  if (v && typeof v === 'object' && 'raw' in v) {
    const d = v as FuzzyDate;
    return d.from?.value ?? d.to?.value ?? null;
  }
  return null;
}

function textOf(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    if ('label' in v && typeof (v as { label: unknown }).label === 'string') return (v as { label: string }).label;
    if ('text' in v) return String((v as RefValue).text ?? '');
  }
  return v === null || v === undefined ? '' : String(v);
}

function fieldMatches(c: Extract<RuleClause, { on: 'field' }>, values: unknown[]): boolean {
  const flat = values.flatMap(v => (Array.isArray(v) ? v : [v])).filter(v => v !== null && v !== undefined && v !== '');
  switch (c.op) {
    case 'exists':
      return flat.length > 0;
    case 'is':
      return flat.some(v => lower(v) === lower(c.value) || lower(textOf(v)) === lower(c.value));
    case 'contains':
      return flat.some(v =>
        textOf(v)
          .toLowerCase()
          .includes(String(c.value ?? '').toLowerCase()),
      );
    case 'gt':
    case 'lt':
      return flat.some(v => {
        const n = numberOf(v);
        return n !== null && (c.op === 'gt' ? n > Number(c.value) : n < Number(c.value));
      });
    case 'after':
    case 'before':
      return flat.some(v => {
        const d = dateOf(v);
        if (!d) return false;
        const ref = String(c.value);
        // Compare on the shared precision: 2021-10 is after 2021 only if it starts after the year does.
        return c.op === 'after' ? d.slice(0, ref.length) > ref : d.slice(0, ref.length) < ref;
      });
  }
}

export function clauseMatches(c: RuleClause, f: StyleFacts, schema: Pick<SchemaSnapshot, 'types'>): boolean {
  let hit: boolean;
  switch (c.on) {
    case 'type':
      hit = typeChain(schema.types, f.typeId).some(t => t.key === c.value);
      break;
    case 'kind':
      hit = f.kind === c.value;
      break;
    case 'field':
      hit = fieldMatches(c, f.fields[c.field] ?? []);
      break;
    case 'tag':
      hit = f.tags.includes(c.value);
      break;
    case 'linked':
      hit = f.links.some(
        l =>
          (!c.relation || l.predicate === c.relation) &&
          (!c.entityId || l.otherId === c.entityId) &&
          (!c.direction || c.direction === 'any' || l.direction === c.direction) &&
          (c.minAmount === null || c.minAmount === undefined || (l.amount !== null && l.amount > c.minAmount)),
      );
      break;
    case 'status':
      hit = f.reviewStatus === c.value;
      break;
    case 'sensitivity':
      hit = f.sensitivity === c.value;
      break;
    case 'confidence':
      hit = f.confidence !== null && (c.op === 'lt' ? f.confidence < c.value : f.confidence > c.value);
      break;
  }
  return c.not ? !hit : hit;
}

export function conditionMatches(cond: RuleCondition, f: StyleFacts, schema: Pick<SchemaSnapshot, 'types'>): boolean {
  if (!cond.clauses.length) return true;
  return cond.match === 'any' ? cond.clauses.some(c => clauseMatches(c, f, schema)) : cond.clauses.every(c => clauseMatches(c, f, schema));
}

/** The list value (and its color) of a field, for "color from the list" rules; values without a color are skipped. */
function listColor(schema: Snap, fieldKey: string, values: unknown[]): { slot: number | null; label: string } | null {
  const f = schema.fields.find(x => x.key === fieldKey);
  const list = f?.enumList ? schema.lists.find(l => l.key === f.enumList) : undefined;
  const keys = values.flatMap(v => (Array.isArray(v) ? v : [v])).filter((v): v is string => typeof v === 'string');
  if (!keys.length) return null;
  const vals = keys.map(k => list?.values.find(v => v.key === k)).filter(v => !!v);
  const colored = vals.find(v => v?.colorSlot);
  const pick = colored ?? vals[0];
  return { slot: pick?.colorSlot ?? null, label: pick?.label ?? String(keys[0]) };
}

/**
 * Rules mode, ordered, first match wins: the fill comes from the first matching color rule, the fade from the
 * first matching fade rule and the ring from the first matching ring rule (different visual channels). Disabled
 * rules are skipped but keep their number.
 */
export function evaluateRules(rules: readonly ColorRule[], f: StyleFacts, schema: Snap): EntityStyle {
  const style: EntityStyle = {
    colorSlot: null,
    why: NO_RULE_MATCHED,
    fade: false,
    fadeWhy: null,
    ring: false,
    ringSlot: null,
    ringWhy: null,
  };
  let colored = false;
  // `rules` are in list order, as the rules API returns them and the editor shows them.
  for (const [i, r] of rules.entries()) {
    if (!r.enabled) continue;
    if (r.action === 'color' && colored) continue;
    if (r.action === 'fade' && style.fade) continue;
    if (r.action === 'ring' && style.ring) continue;
    const fromList =
      r.action === 'color' && r.colorFromField ? listColor(schema, r.colorFromField, f.fields[r.colorFromField] ?? []) : null;
    if (r.action === 'color' && r.colorFromField && !fromList) continue;
    if (!conditionMatches(r.condition, f, schema)) continue;
    const label = ruleLabel(r, i + 1, schema);
    if (r.action === 'color') {
      colored = true;
      if (fromList) {
        const field = schema.fields.find(x => x.key === r.colorFromField)?.label ?? r.colorFromField;
        style.colorSlot = fromList.slot ?? r.colorSlot;
        style.why = `${label} · ${field} = ${fromList.label}${style.colorSlot ? '' : ' (no color)'}`;
      } else {
        style.colorSlot = r.colorSlot;
        style.why = label;
      }
    } else if (r.action === 'fade') {
      style.fade = true;
      style.fadeWhy = label;
    } else {
      style.ring = true;
      style.ringSlot = r.colorSlot;
      style.ringWhy = label;
    }
  }
  return style;
}

/** Type mode: the type's palette slot (sub-types inherit their parent's). */
export function typeStyle(schema: Pick<SchemaSnapshot, 'types'>, typeId: string): EntityStyle {
  const t = schema.types.find(x => x.id === typeId);
  return {
    colorSlot: effectiveColorSlot(schema.types, typeId),
    why: `Color by type: ${t?.label ?? 'unknown type'}`,
    fade: false,
    fadeWhy: null,
    ring: false,
    ringSlot: null,
    ringWhy: null,
  };
}

/** Cluster mode: the cluster's palette slot (grey beyond the 12 largest clusters, or when in none). */
export function clusterStyle(cluster: ClusterRef | null | undefined): EntityStyle {
  return {
    colorSlot: cluster?.colorSlot ?? null,
    why: clusterWhy(cluster),
    fade: false,
    fadeWhy: null,
    ring: false,
    ringSlot: null,
    ringWhy: null,
  };
}

/**
 * The look of an entity in the active mode. Rules-mode styles are computed on the server (cached per entity)
 * and arrive with the entity as `style`, and its cluster as `cluster`; until they do, the entity shows grey
 * rather than a wrong color.
 */
export function styleFor(
  mode: ColorMode,
  schema: Pick<SchemaSnapshot, 'types'>,
  e: { typeId: string; style?: EntityStyle | null; cluster?: ClusterRef | null },
): EntityStyle {
  if (mode === 'rules') return e.style ?? { ...typeStyle(schema, e.typeId), colorSlot: null, why: NO_RULE_MATCHED };
  if (mode === 'cluster') return clusterStyle(e.cluster);
  return typeStyle(schema, e.typeId);
}

/** Full "why this color?" text: fill, then fade and ring when they apply. */
export const whyText = (s: EntityStyle) =>
  [s.why, s.fade ? `Faded: ${s.fadeWhy}` : null, s.ring ? `Ring: ${s.ringWhy}` : null].filter(Boolean).join(' · ');

/** FNV-1a, base 36: a short signature for cache keys. */
export function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/** Changes to rules, or to what their descriptions and list colors read from the schema, make cached styles stale. */
export const rulesSignature = (rules: readonly ColorRule[], schema: Snap) =>
  fnv1a(
    JSON.stringify([
      rules.map(r => [r.id, r.order, r.name, r.condition, r.action, r.colorSlot, r.colorFromField, r.enabled]),
      schema.types.map(t => [t.id, t.key, t.label, t.parentTypeId]),
      schema.fields.map(f => [f.key, f.label, f.enumList]),
      schema.lists.map(l => [l.key, l.values.map(v => [v.key, v.label, v.colorSlot])]),
      schema.relationTypes.map(r => [r.key, r.label]),
    ]),
  );

/** The seeded "Color by Camp" preset (PLAN §2, §12.2): Camp colors come from the Camp list. */
export const COLOR_BY_CAMP_PRESET: ColorRuleInput = {
  name: 'Color by Camp',
  condition: { match: 'all', clauses: [{ on: 'field', field: 'camp', op: 'exists' }] },
  action: 'color',
  colorSlot: null,
  colorFromField: 'camp',
  enabled: true,
};
